import {
    BOX_TRUCK_DAT_SUBTYPE_GROUPS,
    BOX_TRUCK_DEFAULT_MAX_POSTED_AGE_HOURS,
    BOX_TRUCK_UNIFIED_EQUIPMENT,
    type BoxTruckCapacityLimits,
    boxTruckDatSelection,
    matchesBoxTruckSylectusCapacity,
    boxTruckDatSubtypeGroupForCode,
    boxTruckLabelsToSylectusSizes,
    boxTruckLabelsToUnifiedEquipment,
    boxTruckVehicleLabel,
    CanonicalLoad,
    subdivisionCodeOrAsSent,
    createDefaultBoxTruckCapacityLimits,
    defaultBoxTruckDatSubtypeCodes,
    expandZonesToStates,
} from '@numeo/types-core'
import { type EffectivePickupWindow, effectivePickupWindow } from '@numeo/types'
import { LoadSearchRequest } from 'features/load-hub/types/api'
import { LocationData } from 'features/load-hub/types/filters'
import { getToday } from 'features/load-hub/utils/date'
import { formatDateAsLocalIso, formatDateForApi } from '@/utils/format-date'

// Request/filter mapping for the Box Truck loadboard search — extracted from useBoxTruckLoads so the hook
// stays focused on state/effects. Pure functions: filters → LoadSearchRequest, and the client-side predicate
// that decides whether a live-pushed load satisfies the active server search (for the live prepend).

// The board searches up to THREE feeds:
//   - SYLECTUS_EMAIL — the primary Sylectus-email feed (DB, offset-paginated the whole way down).
//   - DAT — a lane-only supplement. Once origin or destination narrows the search, LIVE one.dat.com FindLoads
//     runs via LIS's DatProvider on the shared token pool. An anywhere-to-anywhere search stays Sylectus-only;
//     sending DAT without either leg asks the external board for a nationwide result set.
//   - BEON — the same lane-only shape, answered from the operator's OWN BeOnCarrier connection. LIS already
//     carries the whole path (`beonProvider`, and BEON mappings for both box-truck classes: SPRINTER_VAN →
//     ['71','69'], BOX_TRUCK → ['68','78','77']), so this board only has to ask for the source. It needs a
//     lane for the SAME reason DAT does and one more: BEON is in LIS's BOARDS_WITHOUT_MULTI_STATE, so it
//     cannot honour a multi-state origin — server-side `getProviders` drops it rather than let a widened
//     nationwide result through, and without a lane at all it would be asked for exactly that.
// NUMEO_SEARCH was a feed here too and was dropped — see SupplementFeeds for the measurement.
// `equipmentTypes` is sent as `unifiedEquipmentTypes` (see buildPayload) and carries the top-level equipment
// scope for every source. `datEquipmentTypes` independently carries the exact DAT checkboxes.
export const SOURCE = 'SYLECTUS_EMAIL'
export const DAT_SOURCE = 'DAT'
/** BeOnCarrier — the operator's OWN connected loadboard, so it spends their session and no Numeo token. */
export const BEON_SOURCE = 'BEON'

/**
 * The request `sources` to leave out for each combination of the supplement switches — see
 * `excludedSourcesFor`, which is how the board picks one.
 *
 * DAT and BEON are the only sources the board can decline to ASK for. `SYLECTUS_EMAIL` must stay in
 * `sources` on every request even when its chip is hiding its rows, and the reasons are server-side and
 * silent:
 *   - LIS decides a request is a box-truck one by `sources.includes('SYLECTUS_EMAIL')` (`isBoxTruckSearch`).
 *     Drop it and `resolveDatEquipmentForRequest` answers with the GENERIC mapping, so a Cargo Van pick
 *     quietly asks DAT for a different code set than the board is built on.
 *   - The DAT daily-quota exemption (`loads/search/controller.ts`) requires the same thing. Without it a
 *     box-truck search starts counting against the operator's DAT quota and the board eventually answers
 *     "Daily limit is reached" for no reason visible on screen.
 * Its chip is therefore a client-side hide and nothing more. Sylectus is our own Postgres, and its query
 * supports the board's independent offset pagination on both the generic and indexed paths.
 *
 * Frozen module constants rather than a fresh Set per render — the board compares against these on every
 * request and re-search.
 */
/**
 * Every feed the board can decline to ASK for. `SYLECTUS_EMAIL` is deliberately absent — see above.
 *
 * A third supplement is this list plus its switch, and nothing else: the excluded set below is DERIVED, so
 * it does not grow a constant per combination (two feeds is four, three is eight), and the board's toggle is
 * built from this same list rather than hand-written per feed.
 */
export const SUPPLEMENT_SOURCES = [DAT_SOURCE, BEON_SOURCE] as const
export type SupplementSource = (typeof SUPPLEMENT_SOURCES)[number]

/** Which supplements this board is currently asking for. */
export type RequestedSupplements = Readonly<Record<SupplementSource, boolean>>

const excludedByCombination = new Map<string, ReadonlySet<string>>()

/**
 * The sources to leave out, given which supplements are being asked for.
 *
 * Memoised per combination rather than built per call, because the board assigns this to a ref on EVERY
 * render (the request gate has to be readable before React has re-rendered with a new switch value).
 * Allocating here would churn a Set per render and, worse, break the identity comparisons the gate relies
 * on. There are 2^n combinations and n is 2, so the cache is bounded and tiny.
 */
export const excludedSourcesFor = (requested: RequestedSupplements): ReadonlySet<string> => {
    const key = SUPPLEMENT_SOURCES.map((source) => (requested[source] ? '1' : '0')).join('')
    let excluded = excludedByCombination.get(key)
    if (!excluded) {
        excluded = new Set(SUPPLEMENT_SOURCES.filter((source) => !requested[source]))
        excludedByCombination.set(key, excluded)
    }
    return excluded
}

/**
 * Whether the board should ASK for BEON.
 *
 * Two conditions, and the second is a tri-state on purpose. `null` means the connection status has not come
 * back yet, and that must NOT gate: a board whose operator has BEON on and working would otherwise drop the
 * feed on its first search and present it as "no loads". `false` means we have LEARNED there is no
 * connection (LIS clears the credential on an auth error), and then asking is pointless — it buys a
 * `source_error` the operator cannot act on, because the row has given up its switch for a Connect button.
 */
export const beonRequestable = (enabled: boolean, connected: boolean | null): boolean => enabled && connected !== false

/**
 * Whether a search on these filters would carry `source` at all, asked of the request builder itself so it
 * cannot drift from the real rule. DAT also needs a lane and a mappable equipment pick, so on an
 * anywhere-to-anywhere board its chip can be on and the feed still never appear.
 */
export const searchWouldCarrySource = (filters: BoxTruckSearchFilters, source: string): boolean =>
    buildRequest(filters, 0, { dat: true, beon: true }).sources?.includes(source) === true

// One 150-row DB page per request (the server's PICKUP_WINDOW_MAX_LIMIT). The board infinite-scrolls:
// each `loadMore` fetches the next OFFSET page of the primary Sylectus feed and APPENDS it (mirrors the
// mobile board), instead of page-number navigation that stranded the operator on a phantom tail page.
export const PAGE_SIZE = 150

/** Lane / date / equipment filters. All optional — empty everywhere = anywhere-to-anywhere. Equipment is
 *  a server-side multi-select (unified codes) sent as `equipmentTypes`, exactly like Load Hub; an empty
 *  selection defaults to the full box-truck class set so the board stays box-truck-only. */
export interface BoxTruckSearchFilters {
    origin: LocationData | null
    destination: LocationData | null
    /** Multi-select origin/destination chips (MultiLocationInput): one city (exclusive) OR several states.
     *  `origin`/`destination` above mirror the first chip (radius, tab label, legacy restore); the request and
     *  the live-prepend predicate read these — a state pick becomes `origin_state = ANY(states)`. Optional so
     *  filters persisted before multi-state still restore (they fall back to the single `origin`). */
    originOptions?: LocationData[]
    destinationOptions?: LocationData[]
    /** Radius (miles) around the picked origin/destination CITY. Only applies when the location carries
     *  coords (a city pick, not a state pick); switches that leg from a state match to a geo radius. */
    originRadius: number | null
    destinationRadius: number | null
    startDate: Date | null
    endDate: Date | null
    /** Selected box-truck equipment LABELS (BOX_TRUCK_EQUIPMENT_LABELS — Cargo Van, Sprinter, …). Mapped for
     *  the request into coarse unified codes (live-DAT half) + raw Sylectus sizes (Sylectus feed).
     *  Empty = the default box-truck set; a pick narrows the DB query on the next Search. */
    equipmentLabels?: string[]
    /** Exact raw DAT subtype choices, judged against the ACTIVE equipment groups: none of them ticked disables
     *  the DAT leg, all of them is no narrowing at all, anything between is sent as an explicit code list. */
    datEquipmentTypes?: string[]
    /** Per-product-group capacity limits owned by the separate Weight & size panel. */
    capacityLimits?: BoxTruckCapacityLimits
    /** Whether a load lacking either capacity field may pass the per-group limits. */
    includeUnknownCapacity?: boolean
    /** Load type. 'BOTH' (the default) is sent as NOTHING — the server's own default — so it never narrows
     *  the query; only an explicit FULL / PARTIAL becomes a filter, on the DB half and in DAT's
     *  `capacity.fullPartial`. */
    fullPartial?: BoxTruckLoadType
    /** Legacy single capacity caps, retained for persisted tabs. DAT receives them as
     *  `capacity.maximumLengthFeet` / `maximumWeightPounds`, the DB half as `length <= n` / `weight <= n`.
     *  A Dry Van pick asks DAT for a wide set of full-size van codes, where deck size varies widely, so these
     *  caps are how an operator narrows it. OPTIONAL by design: gating the code set on them made the board's
     *  reach depend on a filter buried in Advanced Filters, which cost far more loads than it saved. Null =
     *  no cap. Per-group missing-field behavior is enforced by `matchesCapacity`. */
    lengthFt?: number | null
    weightLbs?: number | null
    /** How far back the SEARCH reaches, as the request's own `maxPostedAgeHours` — the server's bound over
     *  the whole matching set, not a cap applied to the page it returns. Set from the Max Age control, which
     *  is why it lives with the lane and the pickup window rather than with the client refinements. */
    maxPostedAgeHours?: number
    /** Find-by-Numeo-ID: when set, the server returns only the matching load (across all pages),
     *  regardless of lane. Derived from the find box when the term looks like a Numeo ID. */
    numeoId?: string | null
}

/** Load-type options offered by the filter bar; 'BOTH' means "don't filter". */
export type BoxTruckLoadType = 'BOTH' | 'FULL' | 'PARTIAL'

/** The operator's capacity filters, on their own — the subset every capacity check reads. */
export type BoxTruckCapacityFilters = Pick<BoxTruckSearchFilters, 'fullPartial' | 'lengthFt' | 'weightLbs' | 'capacityLimits' | 'includeUnknownCapacity'>

/**
 * Whether a load satisfies the capacity filters. The SINGLE definition of that comparison: the live-prepend
 * predicate and the board's Sylectus refinement both call it, so a change to a rule (an operator, a
 * null-handling tweak) can never land in one and be forgotten in the other.
 *
 * Legacy global caps keep unknown Sylectus fields, matching the server. Set per-group values refine only the
 * Box Truck Sylectus and DAT feeds; unset values and every unrelated provider pass unchanged.
 */
export const matchesCapacity = (load: CanonicalLoad, f: BoxTruckCapacityFilters): boolean => {
    const isSylectus = load.sourceType === SOURCE
    const isDat = load.sourceType === DAT_SOURCE
    // These controls own the Box Truck Sylectus and DAT feeds. Other providers already apply their own
    // request filters, and reinterpreting their raw equipment codes here silently removes valid rows.
    if (!isSylectus && !isDat) return true

    // Sylectus is exempt from these server predicates because it never reports trailer length or load type, so
    // the fail-open comparison happens here. Shared with the mobile board, which asks the same question of the
    // same feed and used to answer it differently.
    if (!matchesBoxTruckSylectusCapacity(load, { fullPartial: f.fullPartial, lengthFt: f.lengthFt, weightLbs: f.weightLbs })) return false
    if (f.capacityLimits) {
        const vehicle = boxTruckVehicleLabel(load.details?.sylectusVehicleSize, load.equipmentType)
        // Label first (Sylectus states one), then the raw DAT code — resolved over the FULL request mapping,
        // not the visible 19. The unselected path still asks DAT for the hidden reefer/multi-mode codes, and
        // matching those against `group.codes` finds nothing, which let those rows skip the limits entirely.
        const group = BOX_TRUCK_DAT_SUBTYPE_GROUPS.find((candidate) => candidate.labels.includes(vehicle)) ?? boxTruckDatSubtypeGroupForCode(load.equipmentType)
        if (group) {
            const limits = f.capacityLimits[group.id]
            const maxWeightLbs = limits.weightLbs
            const maxLengthFt = limits.lengthFt
            const hasWeightLimit = maxWeightLbs != null
            const hasLengthLimit = maxLengthFt != null
            if (hasWeightLimit || hasLengthLimit) {
                // "Unknown" must not read as "too big" — the rule the rest of this board has always kept, and
                // DAT omits a stated weight often enough that the opposite default silently empties the feed
                // the moment any limit is set. Dropping those rows stays possible, but only when asked for.
                const missingActiveCapacity = (hasWeightLimit && load.weightLbs == null) || (hasLengthLimit && load.equipmentLengthFt == null)
                if (isDat && missingActiveCapacity && f.includeUnknownCapacity === false) return false
                if (maxWeightLbs != null && load.weightLbs != null && load.weightLbs > maxWeightLbs) return false
                if (maxLengthFt != null && load.equipmentLengthFt != null && load.equipmentLengthFt > maxLengthFt) return false
            }
        }
    }
    return true
}

/** A known load type, else 'BOTH' — guards the untrusted persisted JSON. Shared by both persistence layers. */
export const loadTypeOrBoth = (v: unknown): BoxTruckLoadType => (v === 'FULL' || v === 'PARTIAL' ? v : 'BOTH')

/** Positive number or null — guards a persisted capacity cap, which can be anything. */
export const capOrNull = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null)

export const EMPTY_FILTERS: BoxTruckSearchFilters = {
    origin: null,
    destination: null,
    originOptions: [],
    destinationOptions: [],
    originRadius: null,
    destinationRadius: null,
    startDate: null,
    endDate: null,
    equipmentLabels: [],
    // Everything ticked, not nothing: an empty selection switches the DAT leg off, and a board that starts
    // there would quietly stop returning DAT loads for every operator who already had the source enabled.
    datEquipmentTypes: defaultBoxTruckDatSubtypeCodes(),
    capacityLimits: createDefaultBoxTruckCapacityLimits(),
    includeUnknownCapacity: true,
    fullPartial: 'BOTH',
    lengthFt: null,
    weightLbs: null,
    maxPostedAgeHours: BOX_TRUCK_DEFAULT_MAX_POSTED_AGE_HOURS,
    numeoId: null,
}

/** The required pickup window defaults to a 7-day span — today .. today + DEFAULT_PICKUP_WINDOW_DAYS. */
const DEFAULT_PICKUP_WINDOW_DAYS = 6

/** today + DEFAULT_PICKUP_WINDOW_DAYS at local midnight — the default UPPER bound of the pickup window. */
export const getDefaultPickupEnd = (): Date => {
    const d = getToday()
    d.setDate(d.getDate() + DEFAULT_PICKUP_WINDOW_DAYS)
    return d
}

/** The pickup-window LOWER bound (startDate): defaults to today and never sits in the past — a missing or
 *  restored-stale (yesterday) pickup snaps to today. Shared by BOTH persistence layers (single filters + the tab
 *  set) so a restored tab can't re-run a search with a stale past window. */
export const sanitizePickup = (d: Date | null): Date => {
    const today = getToday()
    return !d || d < today ? today : d
}

/** The pickup-window UPPER bound (endDate): defaults to today + DEFAULT_PICKUP_WINDOW_DAYS when UNSET (so a fresh
 *  board opens on a one-week window, not a single day), snaps a restored-stale (past) end up to today, and keeps
 *  any value the operator explicitly picked. */
export const sanitizePickupEnd = (d: Date | null): Date => {
    if (!d) return getDefaultPickupEnd()
    const today = getToday()
    return d < today ? today : d
}

/**
 * Build the origin/destination payload from a picked location and its optional radius.
 *  - With a radius on a CITY pick (coords present) → a geo radius (`latitude/longitude/dhd`). We send NO
 *    `states` because the server prioritises states over coords (queryLoadsBySource), which would ignore
 *    the radius. LIS keeps the SYLECTUS_EMAIL feed on queryLoadsBySource (ST_DWithin radius) rather than the
 *    pickup-window fast path — the fast path would default equipment to ['V'] and pin the pickup window to
 *    today→tomorrow, dropping every non-dry-van and future-pickup load.
 *  - Otherwise, when a state is known → an EXACT state match (`states: [XX]`), filtered as
 *    `origin_state = ANY(states)`.
 */
/** The picked chips for a leg — the multi-select options, falling back to the legacy single `location` (older
 *  persisted filters that predate multi-state) so a restore never loses the lane. `Array.isArray` guards
 *  untrusted persisted JSON. Exported so persistence/tabs/tab-labels resolve chips the exact same way. */
export const legOptions = (options: LocationData[] | undefined, single: LocationData | null): LocationData[] =>
    Array.isArray(options) && options.length > 0 ? options : single ? [single] : []

/** The uppercased, de-duplicated state codes across a leg's chips. A single city contributes its own state; a
 *  ZONE chip (a DAT zone — no single stateProv) expands to its member states, so the existing multi-state
 *  backend path (origin_state = ANY(...)) handles zones with no server change. */
const legStates = (opts: LocationData[]): string[] => {
    const states = new Set<string>()
    for (const o of opts) {
        // Converted here because the board is the LAST place a chip can be caught, and some chips are its
        // own: filters are persisted per window and restored on load, so an operator who once searched with
        // a driver's `Texas` gets it handed back on every reload — a server-side fix never reaches them.
        const stated = subdivisionCodeOrAsSent(o.stateProv)
        if (stated) states.add(stated.toUpperCase())
        // Prefer the member states already carried on the chip; fall back to expanding the zone code.
        const zoneStates = o.zoneStates ? o.zoneStates.split(',') : o.zoneCode ? expandZonesToStates([o.zoneCode]) : []
        for (const s of zoneStates) {
            const code = s.trim().toUpperCase()
            if (code) states.add(code)
        }
    }
    return [...states]
}

const toLocationPayload = (
    options: LocationData[] | undefined,
    single: LocationData | null,
    radiusMiles: number | null
): NonNullable<LoadSearchRequest['origin']> | undefined => {
    const opts = legOptions(options, single)
    // A single CITY pick (coords, always the lone chip since cities are exclusive) + radius → geo radius; NO
    // states (the server prioritises states over coords, which would ignore the radius).
    const city = opts.find((o) => o.latitude != null && o.longitude != null)
    if (radiusMiles && radiusMiles > 0 && city?.latitude != null && city?.longitude != null) {
        // Same reason as `legStates`: a restored or driver-built chip is the one that carries a full name.
        return { latitude: city.latitude, longitude: city.longitude, dhd: radiusMiles, city: city.city, state: subdivisionCodeOrAsSent(city.stateProv) }
    }
    // Otherwise → an exact match on EVERY selected state (`origin_state = ANY(states)`).
    const states = legStates(opts)
    if (states.length === 0) return undefined
    return { state: states[0], states }
}

/** The leg's picks that carry coordinates — one per driver when several are selected. Deduped by rounded
 *  position so two drivers parked at the same yard don't produce two identical searches. */
const pinnedPoints = (opts: LocationData[]): LocationData[] => {
    const seen = new Set<string>()
    const out: LocationData[] = []
    for (const o of opts) {
        if (o.latitude == null || o.longitude == null) continue
        const key = `${o.latitude.toFixed(3)},${o.longitude.toFixed(3)}`
        if (seen.has(key)) continue
        seen.add(key)
        out.push(o)
    }
    return out
}

/**
 * The searches this board has to run for one press of Search — one per reachable driver lane. Equipment
 * subtype codes are unioned into that lane's single DAT request, so they are applied before DAT's result limit
 * without multiplying the Sylectus query, SSE streams or shared DAT-token usage.
 *
 * Picking three trucks asks what those three can take: a radius around each fix. The endpoint searches a
 * single circle, so three circles means three requests, merged into one board by the caller. The board used
 * to collapse the picks to the STATES the trucks sat in and drop the radius, which answered a different
 * question — a 300-mile-wide state around a truck that can realistically serve 150.
 *
 * Anything without coordinates (a fix that never reverse-geocoded, a hand-picked state) travels together on
 * ONE extra combined request, so a state lane still costs exactly one search — and, more importantly, is
 * still searched. Picking three trucks where one never geocoded must not quietly become a two-truck search:
 * that driver's state chip is exactly the fallback the picker put there so they would still contribute.
 */
/**
 * The pickup range this board searches for the given filters, or null when it filters no dates.
 *
 * One derivation, read by the request builder and — through the SNAPSHOT the caller keeps — by the live-push
 * mirror below. They used to answer the date question separately, each from the filters, which is how the
 * board ended up judging its live feed by a range it never searched.
 *
 * `now` is a parameter because the answer depends on it: past days clamp forward to today. Called fresh on
 * every live event it would silently re-clamp at local midnight, moving the board's live scope onto a day its
 * query never ran for. So the caller resolves this ONCE per search and holds the result until the next one.
 */
export const boxTruckPickupWindow = (f: BoxTruckSearchFilters, now?: Date): EffectivePickupWindow | null => {
    const earliest = f.startDate ?? f.endDate
    const latest = f.endDate ?? f.startDate
    return earliest && latest ? effectivePickupWindow(earliest, latest, now) : null
}

/**
 * The live-search identity of one TAB's board, and of one lane within it.
 *
 * Deterministic, because the server replaces a subscription by (user, project, surface, client instance): a
 * reload that reuses the same ids atomically takes over its previous subscriptions instead of leaving them
 * live until TTL. Both live here so the format has exactly one owner — the page needs the board id, and the
 * tab-close path needs to derive the same one to release that board's lanes.
 */
export const boardSearchInstanceId = (tabId: string): string => `box-truck-${tabId}`
export const laneSearchInstanceId = (boardInstanceId: string, laneIndex: number): string => `${boardInstanceId}-lane${laneIndex}`

/**
 * How many searches one press of Search will run.
 *
 * Derived FROM the builder, not alongside it. Counting origin chips with coordinates looks equivalent
 * and is not: `pinnedPoints` collapses two chips at one position, and a cleared radius runs a single
 * state request however many points are selected. An analytics property that re-derives this reports
 * "4-6" for a search that ran one lane — the exact number it exists to measure.
 */
export const countOriginLanes = (filters: BoxTruckSearchFilters): number => buildRequests(filters, 0, { dat: false }).length

export const buildRequests = (
    filters: BoxTruckSearchFilters,
    offset: number,
    supplements: SupplementFeeds,
    /** The range this search runs on, resolved by the caller so the request and the live gate share ONE value.
     *  Omitted (tests, and any caller with no live feed to keep in step) resolves it here from the same rule.
     *  NOT named `window`: inside these builders that shadows the DOM global, and a truthy one reads as a
     *  pickup range whose every field is undefined. */
    pickupWindow: EffectivePickupWindow | null = boxTruckPickupWindow(filters),
    /** The board's live-search identity. Omitted → no lane asks for a subscription (a page of an existing
     *  search, or a caller with no live feed at all). */
    boardInstanceId?: string
): LoadSearchRequest[] => {
    const live = (laneIndex: number) => (boardInstanceId ? laneSearchInstanceId(boardInstanceId, laneIndex) : undefined)
    const options = legOptions(filters.originOptions, filters.origin)
    const points = pinnedPoints(options)
    const perDriver = filters.originRadius != null && filters.originRadius > 0 && points.length > 1
    if (!perDriver) return [buildRequest(filters, offset, supplements, pickupWindow, live(0))]
    const lanes = points.map((point, index) =>
        buildRequest({ ...filters, origin: point, originOptions: [point] }, offset, supplements, pickupWindow, live(index))
    )
    // Only the chips `pinnedPoints` could not turn into a circle. A coordinate chip it dropped as a duplicate
    // is NOT one of these — the point it duplicates already has its own lane.
    const uncharted = options.filter((o) => o.latitude == null || o.longitude == null)
    if (uncharted.length > 0)
        lanes.push(buildRequest({ ...filters, origin: uncharted[0], originOptions: uncharted }, offset, supplements, pickupWindow, live(lanes.length)))
    return lanes
}

/** Which supplement feeds to union alongside the primary Sylectus feed on this page. Live DAT reports no
 *  page-full signal, so the hook only asks for it on page 1.
 *
 *  NUMEO_SEARCH used to ride along here and was removed: on this board it never returned a load. Its leg is
 *  a Smart Search across EVERY ingested source, which on a box-truck query is dense enough to hit the
 *  service's statement timeout — a measured prod search came back
 *  `source_error NUMEO_SEARCH: canceling statement due to statement timeout` with zero rows, having spent
 *  the whole timeout budget while the operator waited. It contributed nothing but latency. */
interface SupplementFeeds {
    dat: boolean
    /** Optional, and deliberately so: BEON is opt-in and OFF by default, so every existing caller that knows
     *  nothing about it keeps asking for exactly the feeds it asked for before. */
    beon?: boolean
}

// Keep SYLECTUS_EMAIL in the source list on every page so its independently paginated feed remains present.
export const buildRequest = (
    filters: BoxTruckSearchFilters,
    offset: number,
    supplements: SupplementFeeds,
    pickupWindow: EffectivePickupWindow | null = boxTruckPickupWindow(filters),
    /** This lane's live-search identity. Omitted → the lane asks for no subscription. */
    liveSearchInstanceId?: string
): LoadSearchRequest => {
    // Coarse unified codes for every source. Three states, NOT two: no selection → the compact Box Truck
    // default (straight truck + Sprinter, deliberately excluding Dry Van); a selection → exactly the classes
    // picked. Falling back to the default on a selection would refill the board with the exact classes the
    // operator filtered out, so an unmappable pick drops the supplements entirely and runs Sylectus-only
    // (narrowed by sylectusVehicleSizes).
    const hasLabels = !!filters.equipmentLabels?.length
    const unified = hasLabels ? boxTruckLabelsToUnifiedEquipment(filters.equipmentLabels ?? []) : []
    const requestUnified = unified.length ? unified : [...BOX_TRUCK_UNIFIED_EQUIPMENT]
    const runSupplements = !hasLabels || unified.length > 0
    const origin = toLocationPayload(filters.originOptions, filters.origin, filters.originRadius)
    const destination = toLocationPayload(filters.destinationOptions, filters.destination, filters.destinationRadius)
    const hasLane = origin != null || destination != null
    // The DAT subtype selection has THREE states, and the middle one is the one that matters.
    //
    //   nothing ticked  → the operator switched the DAT leg off; no DAT source, no call.
    //   everything ticked → not a narrowing. The field is omitted so the server applies the board's own group
    //     mapping, which includes the reefer / multi-mode codes the selector deliberately hides (`SC`, `FR`,
    //     `IR`, `RV`, `VF`, `VR`, `VT`). Sending the visible 19 instead would quietly cost those loads, so an
    //     untouched picker reaches exactly as far as the board did before the picker existed.
    //   some ticked → a real narrowing, sent as an explicit list, intersected with the active equipment groups
    //     so a stale persisted pick cannot spend a DAT call on codes the current equipment scope excludes.
    // Scoped to the active equipment groups, and shared with the filter badges so the indicator and the
    // request can never disagree about whether the operator narrowed anything.
    const { requested: requestedDatCodes, narrowed: datNarrowed } = boxTruckDatSelection(filters.equipmentLabels ?? [], filters.datEquipmentTypes ?? [])
    // Nothing in scope ticked covers both "the operator cleared the picker" and "a stale pick no longer
    // overlaps the equipment" — either way there is no DAT search to run.
    const wantsDat = runSupplements && supplements.dat && hasLane && requestedDatCodes.length > 0
    // No DAT-subtype equivalent: the BEON leg carries only the board's top-level `equipmentTypes`, which LIS
    // translates through the shared BEON mapping. There is no second, finer BEON picker to intersect with.
    const wantsBeon = runSupplements && supplements.beon === true && hasLane
    const supplementSources = [...(wantsDat ? [DAT_SOURCE] : []), ...(wantsBeon ? [BEON_SOURCE] : [])]

    const request: LoadSearchRequest = {
        // Every feed paginates independently off the SAME offset (each source runs its own LIMIT/OFFSET query
        // server-side). Each supplement is unioned in while its own gate holds (its last page came back full)
        // and both drop for a no-unified-class pick (runSupplements false); Sylectus stays in on every page to
        // preserve Sylectus' independent pagination.
        sources: [SOURCE, ...supplementSources],
        // A pick can resolve to DRY_VAN, which is OUTSIDE BOX_TRUCK_UNIFIED_EQUIPMENT — the exemption on the
        // LIS side covers the board's own classes, not that constant, so this is safe. With a fine Sylectus
        // selection the server keeps these canonical codes as the indexed coarse filter, then applies the
        // exact raw distinction (cargo van vs sprinter, small vs large straight, tractor) below.
        equipmentTypes: requestUnified,
        // Draw DAT from the shared token pool, not the operator's personal DAT account: box-truck operators
        // needn't have their own DAT connection. DAT is omitted above until at least one lane leg exists.
        usePersonalDatToken: false,
        // Box Truck renders the in-hand DAT response; defer persistence/enrichment to the
        // unified streaming path so it does not block the loading state.
        skipDatProcessing: true,
        // The operator's Max Age, as the server's own bound: it narrows the MATCHING SET, so the 150-row page
        // is drawn from fresher freight, instead of being a second filter over a page already chosen.
        maxPostedAgeHours: filters.maxPostedAgeHours ?? BOX_TRUCK_DEFAULT_MAX_POSTED_AGE_HOURS,
        limit: PAGE_SIZE,
        offset,
        // Attribute this board's pooled-DAT draws to Box Truck in the `dat_token_usage` rollup
        // (they used to be indistinguishable from Load Hub's under 'unknown'). Reporting only —
        // SYLECTUS_EMAIL access here is still decided by the operator allowlist server-side.
        trigger: 'box_truck',
    }

    if (wantsDat && datNarrowed) request.datEquipmentTypes = requestedDatCodes

    // Find-by-Numeo-ID is a GLOBAL lookup: match the id across all loads and deliberately ignore the
    // lane/date filters — otherwise a matching load on a different lane returns zero rows and reads as
    // "not found". It's a `details->>'numeoId'` match that only SYLECTUS_EMAIL loads carry, so scope the
    // lookup to that source. Return early so origin/destination/pickup are never ANDed in.
    if (filters.numeoId) {
        request.sources = [SOURCE]
        request.equipmentTypes = undefined
        request.maxPostedAgeHours = 0
        request.numeoId = filters.numeoId
        return request
    }

    // Ask for live delivery of this lane. Deliberately AFTER the Numeo-ID early return: that lookup ignores
    // the lane and date filters on purpose, so a subscription built from it would describe a market nobody
    // is looking at. `intent` is omitted so an auto-refresh RENEWS the lane's subscription rather than
    // replacing it (see LoadSearchRequest.liveSearch).
    if (liveSearchInstanceId) request.liveSearch = { surface: 'box_truck', clientSearchInstanceId: liveSearchInstanceId }

    // Fine box-truck equipment filter for the Sylectus feed: raw vehicle sizes for the picked labels
    // (server matches details->>'sylectusVehicleSize'). No pick → no fragment (whole box-truck feed).
    const sylectusVehicleSizes = boxTruckLabelsToSylectusSizes(filters.equipmentLabels ?? [])
    if (sylectusVehicleSizes.length) request.sylectusVehicleSizes = sylectusVehicleSizes

    // Load type — sent only when it actually narrows. Omitting 'BOTH' leaves the server on its own default
    // (and DAT's `capacity.fullPartial` on 'BOTH'), which is also what keeps loads that state neither.
    if (filters.fullPartial === 'FULL' || filters.fullPartial === 'PARTIAL') request.fullPartial = filters.fullPartial

    // Preserve the existing single-cap request contract for older persisted tabs. Per-group Weight & size
    // edits are source-specific client refinements; sending their suggested values here would add length/weight DB
    // predicates to every default search and change its query plan without the operator applying a filter.
    if (filters.lengthFt != null && filters.lengthFt > 0) request.length = filters.lengthFt
    if (filters.weightLbs != null && filters.weightLbs > 0) request.weight = filters.weightLbs

    if (origin) request.origin = origin
    if (destination) request.destination = destination

    if (pickupWindow) {
        // RESOLVED ONCE — ordered, and past days clamped forward exactly as the server would — then every
        // field below is formatted from the SAME two days. Deriving any of them separately is what let a
        // crossed range name one window in the calendar pair and another in the instants, and a past range
        // leave the live feed judging a range the query never covered.
        const { first, last, fromIso, toIso } = pickupWindow
        request.pickup = {
            earliestAvailability: formatDateForApi(first),
            latestAvailability: formatDateForApi(last),
            earliestAvailabilityIso: formatDateAsLocalIso(first),
            latestAvailabilityIso: formatDateAsLocalIso(last),
            // The same days as ONE half-open window of instants, which is what the server's ASAP rule tests
            // "now" against. Built here rather than derived server-side: only this browser knows where its
            // days actually end, and a DST day is not 24 hours long. Shared with mobile so the two boards
            // cannot answer "is today in range" differently. The Iso pair above keeps its TQL meaning.
            fromIso,
            toIso,
        }
    }

    return request
}

/**
 * The picked-equipment gate on its own, because it is the ONE dimension of this board's search that the
 * server's live matcher does not evaluate.
 *
 * `sylectusVehicleSizes` is part of a live search's identity (it is in the criteria hash) but
 * `getLiveSearchMismatchReason` only tests the coarse canonical equipment codes — a Cargo Van and a Sprinter
 * are both `V` there. So a live-delivered posting is authoritative on lane, date, capacity and source, and
 * still has to clear this one check before it reaches a board filtered to a single box-truck class.
 */
export const matchesEquipmentLabels = (load: CanonicalLoad, f: BoxTruckSearchFilters): boolean =>
    !f.equipmentLabels?.length || f.equipmentLabels.includes(boxTruckVehicleLabel(load.details?.sylectusVehicleSize, load.equipmentType))
